import React, { useEffect, useState } from "react"
import { DateTime } from "luxon"
import VacationsHeader from "../common/VacationsHeader.controller"
import TeamMemberVacationsRequests from "./TeamMemberVacationsRequests.controller"
import TeamMemberVacationsResume from "./TeamMemberVacationsResume.controller"
import CalendarTeamVacationRequests from "./CalendarTeamMemberVacationRequests.controller"
import MobileTeamMemberData from "./MobileTeamMemberData.controller"
import ModalCreateMyVacationRequest from "./ModalCreateMyVacationRequest.controller"
import ModalMyVacationsRequestReview from "./ModalMyVacationsRequestReview.controller"
import ModalChangeMyVacationPendingRequest from "./ModalChangeMyVacationPendingRequest.controller"
import ModalChangeMyVacationApprovedRequest from "./ModalChangeMyVacationApprovedRequest.controller"
import ModalCancelMyVacationApproved from "./ModalCancelMyVacationApproved.controller"
import {
  VACATION_PERIOD_NAVIGATOR_MODES,
  MY_VACATION_STATES,
} from "../../../../consts/modules/vacations.consts"
import { BREAKPOINTS } from "../../../../consts/responsiveBreakpoints.consts"
import { getTeamMemberInfoWithUID } from "../../../../services/teamMember.service"
import useScreenDimensionsContext from "../../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import { useSession } from "../../../../hooks/auth/useSession.hook"

/** @typedef {typeof MY_VACATION_STATES[keyof typeof MY_VACATION_STATES]} MyVacationStates */

const MyVacations = ({ mode = VACATION_PERIOD_NAVIGATOR_MODES.PERIOD }) => {
  const [loading, setLoading] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState([
    DateTime.now().startOf("year"),
    DateTime.now().endOf("year"),
  ])

  const [modalPeriod, setModalPeriod] = useState([
    DateTime.now().startOf("year"),
    DateTime.now().endOf("year"),
  ])
  const [refresh, setRefresh] = useState(false)
  const [visibleModal, setVisibleModal] = useState(
    /** @type {MyVacationStates | null} */ (null)
  )
  const [selectedVacationRequest, setSelectedVacationRequest] = useState(null)
  const [hasAvailableVacationDays, setHasAvailableVacationDays] = useState(true)
  const [holidaySchemaId, setHolidaySchemaId] = useState(null)
  const triggerRefresh = () => setRefresh(!refresh)
  const { screenWidth } = useScreenDimensionsContext()
  const { user } = useSession()

  useEffect(() => {
    updateTeamMemberBucketDaysTest()
    if (
      mode === VACATION_PERIOD_NAVIGATOR_MODES.PERIOD &&
      user?.workAnniversary
    ) {
      let anniversaryDate = DateTime.fromISO(user.workAnniversary).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
      const today = DateTime.local().set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
      const currentYear = today.year
      anniversaryDate = anniversaryDate.set({ year: currentYear })

      if (today < anniversaryDate) {
        const firstDate = anniversaryDate.set({ year: currentYear - 1 })
        let secondDate = anniversaryDate
        secondDate = secondDate.minus({ days: 1 })
        setSelectedPeriod([firstDate, secondDate])
        setModalPeriod([firstDate, secondDate])
      } else {
        const firstDate = anniversaryDate
        let secondDate = anniversaryDate
        secondDate = secondDate.plus({ year: 1 })
        secondDate = secondDate.minus({ days: 1 })
        setSelectedPeriod([firstDate, secondDate])
        setModalPeriod([firstDate, secondDate])
      }
    }
  }, [mode, user])

  const updateTeamMemberBucketDaysTest = async () => {
    const userResponse = await getTeamMemberInfoWithUID(user.id)

    if (!userResponse.success) {
      throw new Error(userResponse.message)
    }

    setHolidaySchemaId(userResponse.result[0].idHolidaySchema)
  }

  /**
   * @param {MyVacationStates | null} visibleModal
   * @param {MyVacationStates[]} keys
   * @returns {boolean}
   */
  const showModal = (visibleModal, keys) => {
    return keys.includes(visibleModal)
  }

  return (
    <>
      <VacationsHeader
        selectedPeriod={selectedPeriod}
        setSelectedPeriod={setSelectedPeriod}
        loading={loading}
        mode={mode}
        setCreateModalVisible={setVisibleModal}
        disabled={!hasAvailableVacationDays || loading}
      />
      <div className="vacation-layout">
        {screenWidth > BREAKPOINTS.TABLET_BREAKPOINT ?
          <>
            <div className="left-container">
              <TeamMemberVacationsRequests
                selectedPeriod={selectedPeriod}
                visibleModal={visibleModal}
                setVisibleModal={setVisibleModal}
                selectedVacationRequest={selectedVacationRequest}
                setSelectedVacationRequest={setSelectedVacationRequest}
                refresh={refresh}
                triggerRefresh={triggerRefresh}
              />
            </div>
            <div className="right-container">
              <TeamMemberVacationsResume
                periodRange={selectedPeriod}
                setLoading={setLoading}
                refresh={refresh}
                setHasAvailableVacationDays={setHasAvailableVacationDays}
                holidaySchemaId={holidaySchemaId}
                teamMemberID={user?.teamMemberID}
                workAnniversary={user?.workAnniversary}
                screenWidth={screenWidth}
              />
              <CalendarTeamVacationRequests
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
                refresh={refresh}
                setVisibleModal={setVisibleModal}
                setSelectedVacationRequest={setSelectedVacationRequest}
                workAnniversary={user?.workAnniversary}
              />
            </div>
          </>
        : <>
            <TeamMemberVacationsResume
              periodRange={selectedPeriod}
              setLoading={setLoading}
              refresh={refresh}
              setHasAvailableVacationDays={setHasAvailableVacationDays}
              holidaySchemaId={holidaySchemaId}
              teamMemberID={user?.teamMemberID}
              workAnniversary={user?.workAnniversary}
              screenWidth={screenWidth}
            />
            <MobileTeamMemberData
              selectedPeriod={selectedPeriod}
              setVisibleModal={setVisibleModal}
              selectedVacationRequest={selectedVacationRequest}
              setSelectedVacationRequest={setSelectedVacationRequest}
              refresh={refresh}
              triggerRefresh={triggerRefresh}
            />
          </>
        }
      </div>
      <ModalCreateMyVacationRequest
        holidaySchemaId={holidaySchemaId}
        visible={showModal(visibleModal, [MY_VACATION_STATES.CREATE])}
        setVisible={setVisibleModal}
        selectedPeriod={modalPeriod}
        triggerRefresh={triggerRefresh}
      />
      <ModalMyVacationsRequestReview
        visible={showModal(visibleModal, [MY_VACATION_STATES.QUERY])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        setSelectedVacationRequest={setSelectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
      <ModalChangeMyVacationPendingRequest
        visible={showModal(visibleModal, [MY_VACATION_STATES.CHANGE_PENDING])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        setSelectedVacationRequest={setSelectedVacationRequest}
        selectedPeriod={modalPeriod}
        triggerRefresh={triggerRefresh}
        holidaySchemaId={holidaySchemaId}
      />
      <ModalChangeMyVacationApprovedRequest
        visible={showModal(visibleModal, [MY_VACATION_STATES.CHANGE_APPROVED])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        setSelectedVacationRequest={setSelectedVacationRequest}
        selectedPeriod={modalPeriod}
        triggerRefresh={triggerRefresh}
        holidaySchemaId={holidaySchemaId}
      />
      <ModalCancelMyVacationApproved
        visible={showModal(visibleModal, [MY_VACATION_STATES.CANCEL_APPROVED])}
        setVisible={setVisibleModal}
        selectedVacationRequest={selectedVacationRequest}
        triggerRefresh={triggerRefresh}
      />
    </>
  )
}

export default MyVacations
